import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, Send, PoundSterling, Zap, MonitorSmartphone, ShieldCheck, Phone, Mail, Star, Clock, BarChart3, Headphones, CreditCard, Plus, Minus, Handshake } from "lucide-react";
import { toast } from "sonner";
import WaveBackground from "@/components/site/WaveBackground";
import useSEO from "@/lib/useSEO";
import { IMG } from "@/lib/images";
import { submitContact } from "@/lib/api";

const sellingPoints = [
  "Right partner matched to your business",
  "Lower fees than your current provider",
  "Modern, fast, reliable terminals",
  "Next-day settlement available",
  "Local Northern Ireland team",
  "Exit fees often covered on switching",
];

const sectors = [
  "Cafés & Bakeries",
  "Restaurants",
  "Pubs & Bars",
  "Pharmacies",
  "Off-licences",
  "Salons & Barbers",
  "Garages",
  "Takeaways",
];

const turnoverBands = [
  "Below £100k",
  "£100k – £180k",
  "£180k – £500k",
  "£500k – £1m",
  "£1m – £3m",
  "Above £3m",
];

const faqs = [
  {
    q: "How quickly will I get a quote?",
    a: "Submit your details and we'll be in touch with a tailored quote within one working day — usually the same day if you submit before 4pm.",
  },
  {
    q: "What if I'm tied into a contract?",
    a: "No problem. Depending on the partner we match you with, your exit fees can often be partially or fully covered (subject to your annual card turnover) — so switching frequently costs you nothing.",
  },
  {
    q: "How long does it take to get set up?",
    a: "Card machines ship next working day after sign-up. We'll be on hand for go-live day to make sure everything's working perfectly.",
  },
  {
    q: "Do you cover all of Northern Ireland?",
    a: "Yes — we're a local Northern Ireland team and we support businesses across NI and the wider UK with on-site installations and remote support.",
  },
];

export default function GetStarted() {
  useSEO({
    title: "Get Started — Free quote in 60 seconds",
    description: "Tell us about your business and we'll send a tailored card payments quote within one working day. UK's #1 payments provider, exit fees covered up to £3,000.",
    path: "/get-started",
  });

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    postcode: "",
    turnover: "",
    hasMachine: "",
    interest: "Card Payments",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim()) e.lastName = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.phone.trim()) e.phone = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const message = [
        `Quote request via /get-started`,
        `Interest: ${form.interest}`,
        form.postcode && `Postcode: ${form.postcode}`,
        form.turnover && `Annual card turnover: ${form.turnover}`,
        form.hasMachine && `Currently has a card machine: ${form.hasMachine}`,
      ]
        .filter(Boolean)
        .join("\n");

      await submitContact({
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        phone: form.phone,
        company: form.company || undefined,
        interest: form.interest,
        message,
      });
      setSubmitted(true);
      toast.success("We've got it — we'll reply within one working day.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="get-started-page">
      {/* HERO + INLINE FORM */}
      <section className="relative overflow-hidden bg-white">
        <WaveBackground variant="accent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-20 pb-16">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            {/* LEFT — pitch */}
            <div className="lg:col-span-7 fade-up">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-50 border border-orange-100">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F]" />
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF5A1F]">
                  Next-working-day quote · No obligation
                </span>
              </div>

              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02] text-gray-900 mt-6">
                Cut queues<br />with faster<br />payments<span className="text-[#FF5A1F]">.</span>
              </h1>

              <p className="mt-7 text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl">
                We work with the <strong className="text-gray-900">right partner for your business</strong>. Send us your details and we'll benchmark your current setup — and reply with a tailored quote within one working day.
              </p>

              <div className="mt-9 grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-xl" data-testid="landing-selling-points">
                {sellingPoints.map((p) => (
                  <div key={p} className="flex items-start gap-2.5">
                    <CheckCircle2 size={18} className="text-[#FF5A1F] shrink-0 mt-0.5" />
                    <span className="text-sm font-medium text-gray-700">{p}</span>
                  </div>
                ))}
              </div>

              {/* Positioning line */}
              <div className="mt-10 flex flex-wrap items-center gap-4 p-4 bg-[#F8F9FA] rounded-2xl border border-gray-100 max-w-xl" data-testid="landing-positioning">
                <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center border border-orange-100">
                  <Handshake size={18} className="text-[#FF5A1F]" />
                </div>
                <p className="text-sm text-gray-700 flex-1">
                  We work with the right partner for <strong className="text-gray-900">your business</strong> — no sales pitch, just honest advice.
                </p>
              </div>

              <div className="mt-10 relative max-w-xl">
                <div className="absolute -inset-6 bg-[#FF5A1F]/10 blur-3xl rounded-full" />
                <div className="relative aspect-[5/3] rounded-3xl overflow-hidden shadow-2xl bg-gray-100">
                  <img
                    src={IMG.cardInUseRetail}
                    alt="Customer paying with a contactless card on a modern terminal"
                    className="w-full h-full object-cover"
                    data-testid="landing-hero-image"
                  />
                </div>
              </div>
            </div>

            {/* RIGHT — quote form */}
            <div className="lg:col-span-5">
              {submitted ? (
                <div
                  data-testid="landing-success"
                  className="bg-white border border-gray-100 rounded-3xl p-10 shadow-xl text-center sticky top-28"
                >
                  <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="text-[#FF5A1F]" size={32} />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-gray-900">We've got it.</h3>
                  <p className="mt-3 text-gray-600">
                    We'll reply within one working day with a tailored quote. Need us sooner? Call <a href="tel:07426821449" className="text-[#FF5A1F] font-semibold">07426 821449</a>.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="landing-form"
                  className="bg-white border border-gray-100 rounded-3xl p-7 md:p-8 shadow-xl sticky top-28"
                >
                  <h3 className="font-display text-2xl font-bold text-gray-900">Get your free quote</h3>
                  <p className="text-sm text-gray-500 mt-1">Drop your details and we'll be in touch with a next-working-day quote.</p>

                  <div className="mt-6 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="First name" required error={errors.firstName}>
                        <input type="text" value={form.firstName} onChange={update("firstName")} className="form-input" placeholder="Jane" data-testid="landing-input-firstname" />
                      </Field>
                      <Field label="Last name" required error={errors.lastName}>
                        <input type="text" value={form.lastName} onChange={update("lastName")} className="form-input" placeholder="Smith" data-testid="landing-input-lastname" />
                      </Field>
                    </div>
                    <Field label="Email" required error={errors.email} hint="We'll send your quote to this address.">
                      <input type="email" value={form.email} onChange={update("email")} className="form-input" placeholder="jane@business.co.uk" data-testid="landing-input-email" />
                    </Field>
                    <Field label="Phone" required error={errors.phone} hint="Our team might reach out to discuss the best option.">
                      <input type="tel" value={form.phone} onChange={update("phone")} className="form-input" placeholder="07000 000000" data-testid="landing-input-phone" />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Business name">
                        <input type="text" value={form.company} onChange={update("company")} className="form-input" placeholder="Your business" data-testid="landing-input-company" />
                      </Field>
                      <Field label="Postcode">
                        <input type="text" value={form.postcode} onChange={update("postcode")} className="form-input" placeholder="BT1 1AA" data-testid="landing-input-postcode" />
                      </Field>
                    </div>
                    <Field label="Annual card turnover">
                      <select value={form.turnover} onChange={update("turnover")} className="form-input" data-testid="landing-input-turnover">
                        <option value="">Select…</option>
                        {turnoverBands.map((b) => (
                          <option key={b}>{b}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Currently have a card machine with another provider?">
                      <div className="flex gap-3" data-testid="landing-input-hasmachine">
                        {["Yes", "No"].map((v) => (
                          <label key={v} className={`flex-1 cursor-pointer text-center px-4 py-2.5 rounded-xl border font-semibold text-sm transition ${form.hasMachine === v ? "bg-[#FF5A1F] text-white border-[#FF5A1F]" : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"}`}>
                            <input type="radio" name="hasMachine" value={v} checked={form.hasMachine === v} onChange={update("hasMachine")} className="sr-only" />
                            {v}
                          </label>
                        ))}
                      </div>
                    </Field>
                    <Field label="What are you interested in?">
                      <select value={form.interest} onChange={update("interest")} className="form-input" data-testid="landing-input-interest">
                        <option>Card Payments</option>
                        <option>Lower my fees</option>
                        <option>Just exploring</option>
                      </select>
                    </Field>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="landing-form-submit"
                    className="mt-7 w-full inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] disabled:opacity-60 rounded-full transition-all shadow-lg shadow-orange-500/25 hover:-translate-y-0.5"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        Send me a Quote <Send size={18} />
                      </>
                    )}
                  </button>

                  <p className="mt-4 text-[11px] text-gray-500 leading-relaxed">
                    By submitting, you agree to be contacted by Fenco Digital regarding your enquiry. We never share your data.
                  </p>

                  <div className="mt-5 pt-5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                    <a href="tel:07426821449" className="flex items-center gap-2 font-semibold text-gray-700 hover:text-[#FF5A1F]">
                      <Phone size={14} /> 07426 821449
                    </a>
                    <a href="mailto:info@fencodigital.com" className="flex items-center gap-2 font-semibold text-gray-700 hover:text-[#FF5A1F]">
                      <Mail size={14} /> Email
                    </a>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR — sectors served */}
      <section className="py-14 bg-[#F8F9FA] border-y border-gray-100" data-testid="landing-sectors">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-gray-500 mb-7">
            Trusted by Northern Ireland businesses across
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {sectors.map((s, i) => (
              <span key={s} data-testid={`sector-${i}`} className="font-display text-base md:text-lg font-bold text-gray-700">
                {s}
                {i < sectors.length - 1 && <span className="ml-8 text-gray-300">·</span>}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US — 4 quick benefits */}
      <section className="py-20 md:py-24 bg-white" data-testid="landing-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-12">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Why Fenco Digital</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-4">
              Real savings. No surprises.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Clock, title: "Fast payments", desc: "Modern terminals built for high-pace, face-to-face service." },
              { icon: BarChart3, title: "Real-time insights", desc: "Live transactions, payout alerts and easy PCI compliance." },
              { icon: Headphones, title: "Award-winning support", desc: "Stellar UK phone support — answered 4× faster than competitors." },
              { icon: CreditCard, title: "Smart payments", desc: "Split bills, add gratuity, take any card type with ease." },
            ].map((b, i) => (
              <div
                key={b.title}
                data-testid={`landing-benefit-${i}`}
                className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center mb-4">
                  <b.icon className="text-[#FF5A1F]" size={20} />
                </div>
                <h4 className="font-display text-lg font-bold text-gray-900">{b.title}</h4>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SWITCHING MADE SIMPLE */}
      <section className="py-20 md:py-28 bg-[#F8F9FA]" data-testid="landing-switching">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Switching made simple</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-4">
                We could even cover<br />your exit fees<span className="text-[#FF5A1F]">.</span>
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-2xl">
                Tied into a contract with another provider? Depending on the partner we match you with, your exit fees can often be <strong className="text-gray-900">partially or fully covered*</strong> — so switching frequently costs you nothing.
              </p>
              <p className="mt-3 text-xs text-gray-500">*Subject to your annual card turnover.</p>

              <div className="mt-8 space-y-4">
                {[
                  ["01", "Drop your details", "Submit the form — takes 60 seconds."],
                  ["02", "Get your tailored quote", "We benchmark your current statement and reply within one working day."],
                  ["03", "Switch in days", "Card machines ship next working day. We're with you for go-live."],
                ].map(([n, t, d]) => (
                  <div key={n} className="flex gap-5 p-5 bg-white border border-gray-100 rounded-2xl">
                    <div className="font-display text-3xl font-extrabold text-[#FF5A1F] shrink-0">{n}</div>
                    <div>
                      <h4 className="font-display text-lg font-bold text-gray-900">{t}</h4>
                      <p className="text-gray-600 mt-1">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img src={IMG.cardInUseCafe} alt="Café staff member taking a contactless payment" className="w-full h-full object-cover aspect-[4/5]" loading="lazy" data-testid="landing-switching-image" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CUSTOMER QUOTE */}
      <section className="py-20 md:py-24 bg-white" data-testid="landing-quote">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Star size={28} className="text-[#FF5A1F] mx-auto mb-4" fill="#FF5A1F" />
          <blockquote className="font-display text-3xl md:text-4xl font-bold tracking-tight text-gray-900 leading-tight">
            "Switching through Fenco was the easiest business decision we made last year. Faster payments, lower fees, and the team actually picks up the phone."
          </blockquote>
          <p className="mt-6 text-sm font-semibold text-gray-700">
            — Local Northern Ireland café owner
          </p>
        </div>
      </section>

      {/* FAQS */}
      <section className="py-20 md:py-24 bg-[#F8F9FA]" data-testid="landing-faqs">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">FAQs</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-4">
            Common questions.
          </h2>

          <div className="mt-10 space-y-3">
            {faqs.map((f, i) => (
              <div
                key={f.q}
                data-testid={`landing-faq-${i}`}
                className="bg-white border border-gray-100 rounded-2xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-gray-50 transition"
                  data-testid={`landing-faq-${i}-toggle`}
                >
                  <span className="font-display text-lg font-bold text-gray-900">{f.q}</span>
                  {openFaq === i ? (
                    <Minus size={20} className="text-[#FF5A1F] shrink-0" />
                  ) : (
                    <Plus size={20} className="text-gray-400 shrink-0" />
                  )}
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-28 bg-[#0a0a0a] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight">
            Ready for fast payments?
          </h2>
          <p className="mt-5 text-lg text-gray-300 max-w-2xl mx-auto">
            Drop your details above or call us — most quotes are back the same day.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <a
              href="tel:07426821449"
              data-testid="landing-final-call"
              className="inline-flex items-center gap-2 px-7 py-3.5 text-base font-semibold text-[#0a0a0a] bg-white hover:bg-[#FF5A1F] hover:text-white rounded-full transition-all hover:-translate-y-0.5"
            >
              <Phone size={18} /> 07426 821449
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 text-base font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-full transition-all hover:-translate-y-0.5"
            >
              Other ways to contact <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        .form-input {
          width: 100%;
          padding: 0.7rem 0.9rem;
          border-radius: 0.6rem;
          border: 1px solid #e5e7eb;
          background: #ffffff;
          font-family: inherit;
          font-size: 0.9rem;
          color: #0a0a0a;
          transition: all 0.2s ease;
        }
        .form-input:focus {
          outline: none;
          border-color: #FF5A1F;
          box-shadow: 0 0 0 3px rgba(255,90,31,0.15);
        }
      `}</style>
    </div>
  );
}

function Field({ label, required, error, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-gray-700 tracking-wide">
        {label}
        {required && <span className="text-[#FF5A1F] ml-1">*</span>}
      </span>
      {hint && <span className="block text-[11px] text-gray-500 mt-0.5">{hint}</span>}
      <div className="mt-1.5">{children}</div>
      {error && <span className="text-xs font-semibold text-red-600 mt-1 block">{error}</span>}
    </label>
  );
}
